import type { TuringMachine, TMState } from './types'

export function createTMState(
  machine: TuringMachine,
  input: string[],
): TMState {
  const tape = new Map<number, string>()
  for (let i = 0; i < input.length; i++) {
    tape.set(i, input[i]!)
  }
  return {
    tape,
    head: 0,
    state: machine.initial,
    step: 0,
    outcome: machine.halts.includes(machine.initial) ? 'result' : 'running',
    matchedTransition: null,
    milestones: 0,
  }
}

export function readTape(state: TMState, blank: string): string {
  return state.tape.get(state.head) ?? blank
}

export function stepTM(state: TMState, machine: TuringMachine): TMState {
  if (state.outcome !== 'running') return state

  const symbol = readTape(state, machine.blank)
  const transition = machine.transitions.find(
    (t) => t.state === state.state && t.read === symbol,
  )

  if (!transition) {
    return { ...state, outcome: 'no-result', matchedTransition: null }
  }

  // Write symbol
  const newTape = new Map(state.tape)
  if (transition.write === machine.blank) {
    newTape.delete(state.head)
  } else {
    newTape.set(state.head, transition.write)
  }

  // Move head
  let newHead = state.head
  if (transition.move === 'L') newHead--
  else if (transition.move === 'R') newHead++

  const newState = transition.next
  const outcome = machine.halts.includes(newState) ? 'result' : 'running'

  return {
    tape: newTape,
    head: newHead,
    state: newState,
    step: state.step + 1,
    outcome,
    matchedTransition: transition,
    milestones: state.milestones + (transition.milestone === 'address-consumed' ? 1 : 0),
  }
}

/** Get tape contents as array, from min to max occupied cell */
export function tapeToArray(
  state: TMState,
  blank: string,
  padding = 2,
): { cells: { position: number; symbol: string }[]; headIndex: number } {
  if (state.tape.size === 0) {
    const cells = []
    for (let i = state.head - padding; i <= state.head + padding; i++) {
      cells.push({ position: i, symbol: blank })
    }
    return { cells, headIndex: padding }
  }

  const positions = [...state.tape.keys(), state.head]
  const min = Math.min(...positions) - padding
  const max = Math.max(...positions) + padding

  const cells = []
  for (let i = min; i <= max; i++) {
    cells.push({ position: i, symbol: state.tape.get(i) ?? blank })
  }

  return { cells, headIndex: state.head - min }
}
